import React from 'react';

/*
 * The landing page's infographic: one EXAMPLE unit explained in four parts,
 * built from markup and v2 tokens (no image). Every number is round and
 * made up, and the figure says so. The drawn marks are aria-hidden; the
 * text beside each one carries the meaning for screen readers.
 *
 * Price scale for parts 1 and 3: $80k to $140k.
 */
const LOW = 80;
const HIGH = 140;
const at = (k) => `${Math.round(((k - LOW) / (HIGH - LOW)) * 1000) / 10}%`;

const ASKING = 130;
const ESTIMATE = 110;
const RANGE = [95, 125];
const COMPS = [98, 104, 108, 112, 116, 122];

function Part({ id, n, title, children }) {
    return (
        <div className="pub2-ex-part" role="group" aria-labelledby={id}>
            <div className="pub2-ex-head">
                <span className="pub2-ex-n" aria-hidden="true">{n}</span>
                <span className="pub2-ex-title" id={id}>{title}</span>
            </div>
            {children}
        </div>
    );
}

function Axis() {
    return (
        <div className="pub2-ex-axis" aria-hidden="true">
            <span>${LOW}k</span><span>${(LOW + HIGH) / 2}k</span><span>${HIGH}k</span>
        </div>
    );
}

export default function ExampleUnit() {
    return (
        <figure className="pub2-example" aria-labelledby="pub2-ex-cap">
            <figcaption className="pub2-ex-cap" id="pub2-ex-cap">
                <span className="pub2-ex-badge">Example unit</span>
                <span className="pub2-ex-unit">Row-crop tractor · 2019 · 4,000 hrs</span>
                <span className="pub2-ex-note">Illustrative numbers, not real dealer data.</span>
            </figcaption>

            <div className="pub2-ex-parts">
                <Part id="pub2-ex-price" n="1" title="Price position">
                    <div className="pub2-scale" aria-hidden="true">
                        <span className="pub2-scale-range" style={{ '--from': at(RANGE[0]), '--to': at(RANGE[1]) }} />
                        <span className="pub2-scale-mark pub2-scale-mark--ai" style={{ '--at': at(ESTIMATE) }} />
                        <span className="pub2-scale-mark pub2-scale-mark--ask" style={{ '--at': at(ASKING) }} />
                    </div>
                    <Axis />
                    <ul className="pub2-ex-legend">
                        <li><i className="pub2-key pub2-key--ask" aria-hidden="true" />Asking <b>${ASKING}k</b></li>
                        <li><i className="pub2-key pub2-key--ai" aria-hidden="true" />AI estimate <b>${ESTIMATE}k</b></li>
                        <li><i className="pub2-key pub2-key--range" aria-hidden="true" />Likely range (P10–P90) <b>${RANGE[0]}k–${RANGE[1]}k</b></li>
                    </ul>
                    <p className="pub2-ex-say">Asking sits above the likely range.</p>
                </Part>

                <Part id="pub2-ex-risk" n="2" title="Sell-speed risk">
                    <div className="pub2-meter" aria-hidden="true">
                        <span /><span /><span className="is-on" />
                    </div>
                    <div className="pub2-meter-l" aria-hidden="true"><span>Low</span><span>Medium</span><span>High</span></div>
                    <p className="pub2-ex-big pub2-ex-big--risk">High · median ~110 more days</p>
                    <p className="pub2-ex-say">In stock 150 days, aging bucket 91–180 days.</p>
                </Part>

                <Part id="pub2-ex-comps" n="3" title="Comps">
                    <div className="pub2-scale pub2-scale--comps" aria-hidden="true">
                        {COMPS.map((k) => <span key={k} className="pub2-comp" style={{ '--at': at(k) }} />)}
                        <span className="pub2-scale-mark pub2-scale-mark--ask" style={{ '--at': at(ASKING) }} />
                    </div>
                    <Axis />
                    <p className="pub2-ex-big">{COMPS.length} comparable listings, ${COMPS[0]}k–${COMPS[COMPS.length - 1]}k</p>
                    <p className="pub2-ex-say">The asking price is above every one of them.</p>
                </Part>

                <Part id="pub2-ex-interest" n="4" title="Buyer interest">
                    <dl className="pub2-ex-stats">
                        <div><dt>Views</dt><dd>120</dd></div>
                        <div><dt>Leads</dt><dd>6</dd></div>
                        <div><dt>Open quotes</dt><dd>1</dd></div>
                    </dl>
                    <div className="pub2-ex-bars" aria-hidden="true">
                        {[30, 45, 40, 60, 55, 70, 50].map((h, i) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <span key={i} style={{ '--h': `${h}%` }} />
                        ))}
                    </div>
                    <p className="pub2-ex-say">Plenty of views, few quotes: points to price, not reach.</p>
                </Part>
            </div>
        </figure>
    );
}
